import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Camera, Paperclip } from 'lucide-react';
import { kgToTonnes, tonnesToKg } from '@container-system/shared';
import * as weighingsApi from '../../api/weighings';
import { uploadDocument, documentUrl } from '../../api/documents';
import { useMasterData } from '../../hooks/useMasterData';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { Combobox } from '../../components/ui/Combobox';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';
import { DocumentScannerModal, type DocumentScannerHandle } from '../scanner/DocumentScannerModal';

export interface WeighingFormPrefill {
  plantId?: string | null;
  dateTime?: string | null;
  gateNumber?: string | null;
  licensePlate?: string | null;
  containerId?: string | null;
  /** OCR-erkannter Container-Code ohne Stammdaten-Treffer - zur Anzeige/Vorbelegung, wenn `containerId` leer ist. */
  containerNameRaw?: string | null;
  materialId?: string | null;
  alloyId?: string | null;
  driverId?: string | null;
  netWeightKg?: number | null;
  remarks?: string | null;
  documentId?: string | null;
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function WeighingFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { plants, containers, materials, alloys, drivers, isLoading: masterDataLoading } = useMasterData();

  const navState = location.state as { prefill?: WeighingFormPrefill; source?: 'OCR' } | null;
  const prefill = navState?.prefill;
  // Nur gesetzt, wenn diese Seite von der OCR-Erfassungsseite aus mit einem Scan-Vorschlag
  // geöffnet wurde (siehe `OcrCapturePage`) - ohne dieses Signal blieb `source` beim Speichern
  // bisher IMMER auf "MANUAL", auch wenn die Werte tatsächlich aus einem OCR-Scan kamen.
  const ocrSource = navState?.source;

  const existingQuery = useQuery({
    queryKey: ['weighings', 'detail', id],
    queryFn: () => weighingsApi.getWeighing(id!),
    enabled: mode === 'edit' && !!id,
  });

  const [plantId, setPlantId] = useState('');
  const [dateTime, setDateTime] = useState(toDatetimeLocalValue(new Date().toISOString()));
  const [gateNumber, setGateNumber] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [containerId, setContainerId] = useState<string | null>(null);
  const [containerNameRaw, setContainerNameRaw] = useState<string | null>(null);
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [alloyId, setAlloyId] = useState<string | null>(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [weightTonnes, setWeightTonnes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const scannerRef = useRef<DocumentScannerHandle>(null);

  // Vorbefüllung: entweder aus einer bestehenden Wiegung (Bearbeiten) oder aus einem OCR-Vorschlag (Neu, von der OCR-Erfassungsseite).
  useEffect(() => {
    if (initialized) return;
    if (mode === 'edit' && existingQuery.data) {
      const w = existingQuery.data;
      setPlantId(w.plantId);
      setDateTime(toDatetimeLocalValue(w.dateTime));
      setGateNumber(w.gateNumber ?? '');
      setLicensePlate(w.licensePlate ?? '');
      setContainerId(w.containerId);
      setMaterialId(w.materialId);
      setAlloyId(w.alloyId);
      setDriverId(w.driverId);
      setWeightTonnes(String(kgToTonnes(w.netWeightKg)));
      setRemarks(w.remarks ?? '');
      setDocumentId(w.documentId);
      setInitialized(true);
    } else if (mode === 'create' && !masterDataLoading) {
      if (prefill) {
        setPlantId(prefill.plantId ?? plants.data?.[0]?.id ?? '');
        if (prefill.dateTime) setDateTime(toDatetimeLocalValue(prefill.dateTime));
        setGateNumber(prefill.gateNumber ?? '');
        setLicensePlate(prefill.licensePlate ?? '');
        setContainerId(prefill.containerId ?? null);
        setContainerNameRaw(prefill.containerNameRaw ?? null);
        setMaterialId(prefill.materialId ?? null);
        setAlloyId(prefill.alloyId ?? null);
        setDriverId(prefill.driverId ?? null);
        if (prefill.netWeightKg != null) setWeightTonnes(String(kgToTonnes(prefill.netWeightKg)));
        setRemarks(prefill.remarks ?? '');
        setDocumentId(prefill.documentId ?? null);
      } else {
        setPlantId(plants.data?.[0]?.id ?? '');
      }
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, existingQuery.data, masterDataLoading]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const input = {
        plantId,
        dateTime: new Date(dateTime).toISOString(),
        gateNumber: gateNumber || null,
        licensePlate: licensePlate || null,
        containerId: containerId!,
        materialId: materialId!,
        alloyId: alloyId || null,
        driverId: driverId || null,
        netWeightKg: tonnesToKg(Number(weightTonnes.replace(',', '.'))),
        remarks: remarks || null,
        documentId,
        ...(mode === 'create' && ocrSource ? { source: ocrSource } : {}),
      };
      if (mode === 'edit' && id) return weighingsApi.updateWeighing(id, input);
      return weighingsApi.createWeighing(input);
    },
    onSuccess: () => {
      toast.show(mode === 'edit' ? 'Wiegung aktualisiert.' : 'Wiegung gespeichert.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['weighings'] });
      navigate('/weighings');
    },
    onError: (err) => setError(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.'),
  });

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const stored = await uploadDocument(file);
      setDocumentId(stored.id);
      toast.show('Beleg hochgeladen.', 'success');
    } catch {
      toast.show('Upload fehlgeschlagen.', 'error');
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void uploadFile(file);
  }

  if (masterDataLoading || (mode === 'edit' && existingQuery.isLoading) || !initialized) return <FullPageSpinner />;

  const containerOptions = (containers.data ?? []).filter((c) => c.isActive || c.id === containerId).map((c) => ({ id: c.id, label: c.name }));
  const materialOptions = (materials.data ?? []).filter((m) => m.isActive || m.id === materialId).map((m) => ({ id: m.id, label: m.name }));
  const alloyOptions = (alloys.data ?? []).filter((a) => a.isActive || a.id === alloyId).map((a) => ({ id: a.id, label: a.name }));
  const driverOptions = (drivers.data ?? []).filter((d) => d.isActive || d.id === driverId).map((d) => ({ id: d.id, label: d.name }));

  const weightValue = Number(weightTonnes.replace(',', '.'));
  const weightInvalid = !weightTonnes || Number.isNaN(weightValue) || weightValue <= 0;
  const containerMissing = !containerId && !containerNameRaw;
  const canSubmit = plantId && containerId && materialId && !weightInvalid;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <PageHeader title={mode === 'edit' ? 'Wiegung bearbeiten' : 'Neue Wiegung erfassen'} />

      <Card>
        <CardBody>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveMutation.mutate();
            }}
          >
            <div className="grid grid-cols-2 gap-4">
              <Select label="Werk" value={plantId} onChange={(e) => setPlantId(e.target.value)} error={!plantId ? 'Pflichtfeld' : null} required>
                <option value="" disabled>
                  Bitte wählen…
                </option>
                {plants.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Input
                label="Datum & Uhrzeit"
                type="datetime-local"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                error={!dateTime ? 'Pflichtfeld' : null}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Tor-/Sende-Nr." value={gateNumber} onChange={(e) => setGateNumber(e.target.value)} />
              <Input label="KFZ-Kennzeichen" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} />
            </div>

            <Combobox
              label="Container"
              options={containerOptions}
              value={containerId}
              onChange={(id) => {
                setContainerId(id);
                if (id) setContainerNameRaw(null);
              }}
              placeholder="Container wählen…"
              unmatchedText={containerNameRaw}
              error={containerMissing ? 'Pflichtfeld' : null}
            />

            <div className="grid grid-cols-2 gap-4">
              <Combobox
                label="Material"
                options={materialOptions}
                value={materialId}
                onChange={setMaterialId}
                placeholder="Material wählen…"
                error={!materialId ? 'Pflichtfeld' : null}
              />
              <Combobox label="Legierung" options={alloyOptions} value={alloyId} onChange={setAlloyId} placeholder="Optional" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Combobox label="Fahrer" options={driverOptions} value={driverId} onChange={setDriverId} placeholder="Optional" />
              <Input
                label="Nettogewicht (t)"
                type="number"
                step="0.001"
                min="0"
                value={weightTonnes}
                onChange={(e) => setWeightTonnes(e.target.value)}
                error={weightInvalid ? 'Pflichtfeld' : null}
                required
              />
            </div>

            <Textarea label="Bemerkung" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />

            <div>
              <span className="text-sm font-medium text-ivory-300">Wiegeschein (optional)</span>
              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => scannerRef.current?.open()}>
                  <Camera className="h-4 w-4" strokeWidth={1.75} /> Scannen
                </Button>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-surface-600 px-3.5 py-2.5 text-sm text-ivory-300 transition-colors hover:border-accent-500 hover:text-ivory-100">
                  <Paperclip className="h-4 w-4 text-ivory-500" strokeWidth={1.75} />
                  {uploading ? 'Lädt hoch…' : 'Datei wählen'}
                  <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={handleFileChange} disabled={uploading} />
                </label>
                {documentId && (
                  <a href={documentUrl(documentId)} target="_blank" rel="noreferrer" className="text-xs font-medium text-accent-400 hover:underline">
                    Dokument ansehen
                  </a>
                )}
              </div>
            </div>

            <DocumentScannerModal ref={scannerRef} onCapture={(file) => void uploadFile(file)} />

            {error && <p className="text-sm text-danger-400">{error}</p>}

            <div className="mt-2 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={!canSubmit} loading={saveMutation.isPending}>
                Speichern
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
